import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FOCUS_PAUSE_REASONS, FOCUS_TIMER_PRESETS, formatTimerDuration } from '../data/focusTimer';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import { IntegrationsAPI } from '../services/integrations';
import { TasksAPI } from '../services/tasks';
import { ClientConsolidationAPI } from '../services/clientConsolidation';
import { ensureAreaOption, ensureOption, formatStage } from '../data/team';
import OperationStatus from './OperationStatus';

const LINK_TYPE_OPTIONS = [
  { value: 'documento', label: 'Documento' },
  { value: 'imagem', label: 'Imagem' },
  { value: 'bling', label: 'Bling' },
  { value: 'auvo', label: 'AUVO' }
];

const IMAGE_MODE_OPTIONS = [
  { value: 'upload', label: 'Arquivo do computador' },
  { value: 'url', label: 'Imagem pela internet' }
];

const PROVIDER_LOOKUP_MODE_OPTIONS = [
  { value: 'customer', label: 'Por cliente' },
  { value: 'manual', label: 'Por número' }
];

function normalizeComparableText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function createProviderOptionsState(overrides = {}) {
  return {
    loaded: false,
    loading: false,
    available: false,
    reason: '',
    customer: null,
    refs: {},
    options: [],
    error: '',
    totalFetched: 0,
    totalAvailable: 0,
    totalExcluded: 0,
    excludedOptions: [],
    mode: 'customer',
    query: '',
    searchStrategy: '',
    ...overrides
  };
}

function createLinkOptionsState() {
  return {
    bling: createProviderOptionsState(),
    auvo: createProviderOptionsState()
  };
}

function formatDateTime(date) {
  if (!date) return '—';
  return new Date(date).toLocaleString('pt-BR');
}

function formatDate(date) {
  if (!date) return 'Sem prazo';
  return new Date(date).toLocaleDateString('pt-BR');
}

function formatOptionDate(date) {
  if (!date) return '';
  return new Date(date).toLocaleDateString('pt-BR');
}

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(amount);
}

function formatFileSize(value) {
  const size = Number(value || 0);
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatExternalRefValue(value) {
  if (Array.isArray(value)) {
    const values = value.map((item) => String(item || '').trim()).filter(Boolean);
    return values.length ? values.join(', ') : 'Nao informado';
  }

  return String(value || '').trim() || 'Nao informado';
}

function buildCustomerReferenceRows(providerState) {
  const refs = providerState?.refs || {};
  const rows = [
    ['Cliente Mestre', refs.masterClientId],
    ['CEC integrado', refs.cecIntegratedId],
    ['CEC integrados', refs.cecIntegratedIds],
    ['Bling ID', refs.blingContatoId],
    ['Bling IDs', refs.blingContatoIds],
    ['AUVO ID', refs.auvoClienteId],
    ['AUVO IDs', refs.auvoClienteIds]
  ];

  return rows.map(([label, value]) => ({ label, value: formatExternalRefValue(value) }));
}

function externalCustomerIdFromOption(provider, option) {
  if (!option) return '';

  return provider === 'bling'
    ? String(option.metadata?.contatoId || option.metadata?.blingContatoId || option.contatoId || '').trim()
    : String(option.metadata?.clienteAuvoId || option.metadata?.customerId || option.clienteAuvoId || '').trim();
}

function providerRefIdsFromState(provider, providerState) {
  const refs = providerState?.refs || {};
  const values = provider === 'bling'
    ? [...(refs.blingContatoIds || []), refs.blingContatoId]
    : [...(refs.auvoClienteIds || []), refs.auvoClienteId];

  return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];
}

function buildUserOptions(users, currentUser, task) {
  const list = [...(users || [])];
  if (currentUser?.id && !list.some((user) => user.id === currentUser.id)) list.unshift(currentUser);
  if (task?.assignee?.id && !list.some((user) => user.id === task.assignee.id)) list.push(task.assignee);
  return list;
}

function getEventLabel(event) {
  switch (event.type) {
    case 'create': return 'Tarefa criada';
    case 'assign': return 'Responsável alterado';
    case 'status': return 'Status alterado';
    case 'stage': return 'Etapa alterada';
    case 'priority': return 'Prioridade alterada';
    case 'area': return 'Área alterada';
    case 'due_date': return 'Prazo alterado';
    case 'comment': return 'Atualização no log';
    case 'link_add': return 'Vínculo adicionado';
    case 'view_start': return 'Tarefa aberta para análise';
    case 'view_end': return 'Tarefa fechada após análise';
    default: return event.type;
  }
}

function getLinkTypeLabel(type) {
  switch (type) {
    case 'imagem': return 'Imagem';
    case 'documento': return 'Documento';
    case 'bling': return 'Bling';
    case 'auvo': return 'AUVO';
    default: return 'Vínculo';
  }
}

function createLinkDraft(type = 'documento') {
  return {
    type,
    label: '',
    id: '',
    url: '',
    mode: 'upload',
    lookupMode: 'customer',
    lookupNumber: '',
    files: [],
    resetToken: `${type}-${Date.now()}-${Math.random()}`
  };
}

function buildManualLinkPayload(link) {
  const url = String(link.url || '').trim();

  switch (link.type) {
    case 'imagem':
      return {
        source: 'url',
        type: 'imagem',
        provider: 'manual',
        label: String(link.label || '').trim() || 'Imagem externa',
        url
      };
    default:
      return null;
  }
}

function buildExternalOptionPayload(type, option) {
  if (!option?.id) return null;

  return {
    source: type,
    type,
    provider: type,
    label: option.label || `${type === 'bling' ? 'Proposta' : 'OS'} ${option.code || option.id}`,
    id: option.id,
    code: option.code || undefined,
    url: option.url || undefined,
    metadata: option.metadata || undefined
  };
}

function validateLinkDraft(link, oneDriveStatus, selectedOption) {
  if (link.type === 'documento' || (link.type === 'imagem' && link.mode === 'upload')) {
    if (!oneDriveStatus?.configured) {
      return 'Configure o OneDrive central na API antes de enviar arquivos.';
    }

    if (!oneDriveStatus?.connected) {
      return 'Conecte o OneDrive central antes de enviar arquivos.';
    }

    if (!Array.isArray(link.files) || !link.files.length) {
      return link.type === 'imagem'
        ? 'Selecione uma ou mais imagens do computador para enviar.'
        : 'Selecione um ou mais documentos do computador para enviar.';
    }

    return '';
  }

  if (link.type === 'imagem') {
    return link.url?.trim() ? '' : 'Informe a URL da imagem.';
  }

  if (link.type === 'bling') {
    return selectedOption?.id ? '' : 'Selecione uma proposta do Bling para vincular.';
  }

  if (link.type === 'auvo') {
    return selectedOption?.id ? '' : 'Selecione uma OS do AUVO para vincular.';
  }

  return 'Tipo de vínculo inválido.';
}

function getProviderEmptyMessage(provider, reason, lookupMode = 'customer') {
  if (lookupMode === 'manual') {
    return provider === 'bling'
      ? 'Nenhuma proposta encontrada para esse número.'
      : 'Nenhuma OS encontrada para esse número.';
  }

  if (reason === 'task_without_customer') {
    return 'Esta tarefa ainda não tem cliente vinculado.';
  }

  if (reason === 'customer_not_found') {
    return 'O cliente desta tarefa não foi encontrado na base local.';
  }

  if (reason === 'missing_customer_ref') {
    return provider === 'bling'
      ? 'Este cliente ainda não possui código do Bling no cadastro integrado.'
      : 'Este cliente ainda não possui código do AUVO no cadastro integrado.';
  }

  return provider === 'bling'
    ? 'Nenhuma proposta disponível para este cliente.'
    : 'Nenhuma OS disponível para este cliente.';
}

function formatLinkedTaskSummary(excludedOptions = []) {
  const linkedTask = excludedOptions.find((option) => option?.linkedTask)?.linkedTask;
  if (!linkedTask?.id) return '';

  const details = [
    linkedTask.title,
    linkedTask.customerName,
    linkedTask.status,
    linkedTask.stage
  ].filter(Boolean);

  return `Já vinculada na tarefa ${details.length ? details.join(' • ') : linkedTask.id}.`;
}

function getLinkDescription(link) {
  const parts = [getLinkTypeLabel(link.type)];

  if (link.provider) {
    parts.push(`via ${String(link.provider).toUpperCase()}`);
  } else if (link.source) {
    parts.push(`fonte ${link.source}`);
  }

  const size = formatFileSize(link.size);
  if (size) parts.push(size);

  return parts.join(' • ');
}

function getOneDriveAccountLabel(status) {
  return status?.account?.email || status?.account?.displayName || 'Conta central';
}

function renderTimelineDetails(event) {
  if (event.type === 'comment') return <div className="mt-2">{event.data?.text}</div>;
  if (event.type === 'assign') return <div className="mt-2">De {event.data?.from?.name || 'ninguém'} para {event.data?.to?.name || 'ninguém'}</div>;
  if (event.type === 'status') return <div className="mt-2">De {event.data?.from} para {event.data?.to}</div>;
  if (event.type === 'stage') return <div className="mt-2">De {formatStage(event.data?.from)} para {formatStage(event.data?.to)}</div>;
  if (event.type === 'priority') return <div className="mt-2">De {event.data?.from || 'sem prioridade'} para {event.data?.to || 'sem prioridade'}</div>;
  if (event.type === 'area') return <div className="mt-2">De {event.data?.from?.name || 'sem área'} para {event.data?.to?.name || 'sem área'}</div>;
  if (event.type === 'due_date') return <div className="mt-2">De {formatDate(event.data?.from)} para {formatDate(event.data?.to)}</div>;
  if (event.type === 'link_add') {
    return (
      <div className="mt-2">
        {event.data?.link?.label || event.data?.link?.fileName || getLinkTypeLabel(event.data?.link?.type)}
      </div>
    );
  }
  if (event.type === 'view_end') {
    const seconds = Math.round(Number(event.data?.durationMs || 0) / 1000);
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return <div className="mt-2">Tempo aberta: {minutes ? `${minutes}min ${rest}s` : `${rest}s`}</div>;
  }
  return null;
}

export default function TaskDrawer({
  task,
  currentUser,
  users,
  workflowOptions,
  focusTimer,
  focusTimerSnapshot,
  onStartFocusTimer,
  onPauseFocusTimer,
  onResumeFocusTimer,
  onFinishFocusTimer,
  onClose,
  onTaskUpdated,
  onUnauthorized,
  canDelete,
  onDeleteTask,
  onOpenWhatsAppAttendance
}) {
  const popupRef = useRef(null);
  const popupPollRef = useRef(null);
  const previousTaskIdRef = useRef(task?._id || null);
  const userOptions = useMemo(() => buildUserOptions(users, currentUser, task), [users, currentUser, task]);
  const stageOptions = useMemo(() => ensureOption(workflowOptions?.stages, task?.stage || 'Entrada'), [workflowOptions, task]);
  const statusOptions = useMemo(() => ensureOption(workflowOptions?.statuses, task?.status || 'Aberta'), [workflowOptions, task]);
  const priorityOptions = useMemo(() => ensureOption(workflowOptions?.priorities, task?.priority || 'Media'), [workflowOptions, task]);
  const areaOptions = useMemo(() => ensureAreaOption(workflowOptions?.areas, task?.area), [workflowOptions, task]);

  const [workflow, setWorkflow] = useState({
    stage: task?.stage || stageOptions[0] || 'Entrada',
    status: task?.status || statusOptions[0] || 'Aberta',
    priority: task?.priority || priorityOptions[0] || 'Media',
    areaCode: task?.area?.code || '',
    dueDate: task?.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '',
    assigneeId: task?.assignee?.id || '',
    assigneeName: task?.assignee?.name || '',
    note: ''
  });
  const [comment, setComment] = useState('');
  const [link, setLink] = useState(() => createLinkDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pauseReason, setPauseReason] = useState(FOCUS_PAUSE_REASONS[0]);
  const [oneDriveStatus, setOneDriveStatus] = useState(null);
  const [oneDriveLoading, setOneDriveLoading] = useState(false);
  const [connectingOneDrive, setConnectingOneDrive] = useState(false);
  const [linkOptions, setLinkOptions] = useState(() => createLinkOptionsState());
  const [reportingLinkId, setReportingLinkId] = useState('');
  const [syncingExternal, setSyncingExternal] = useState('');
  const [linkingExternalCustomer, setLinkingExternalCustomer] = useState(false);
  const [activeTab, setActiveTab] = useState('resumo');

  function clearPopupPoll() {
    if (popupPollRef.current) {
      window.clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }

  function setProviderOptions(provider, updates) {
    setLinkOptions((current) => ({
      ...current,
      [provider]: {
        ...current[provider],
        ...updates
      }
    }));
  }

  async function loadOneDriveStatus({ surfaceError = false } = {}) {
    setOneDriveLoading(true);

    try {
      const status = await IntegrationsAPI.oneDriveStatus();
      setOneDriveStatus(status);
      return status;
    } catch (requestError) {
      if (isUnauthorizedError(requestError)) {
        onUnauthorized?.();
        return null;
      }

      const message = getErrorMessage(requestError, 'Não foi possível consultar o OneDrive central.');
      setOneDriveStatus((current) => current || {
        configured: false,
        connected: false,
        missing: [],
        lastError: message
      });

      if (surfaceError) setError(message);
      return null;
    } finally {
      setOneDriveLoading(false);
    }
  }

  useEffect(() => {
    const nextTaskId = task?._id || null;
    const taskChanged = previousTaskIdRef.current !== nextTaskId;
    previousTaskIdRef.current = nextTaskId;

    setWorkflow({
      stage: task?.stage || stageOptions[0] || 'Entrada',
      status: task?.status || statusOptions[0] || 'Aberta',
      priority: task?.priority || priorityOptions[0] || 'Media',
      areaCode: task?.area?.code || '',
      dueDate: task?.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '',
      assigneeId: task?.assignee?.id || '',
      assigneeName: task?.assignee?.name || '',
      note: ''
    });

    if (taskChanged) {
      setComment('');
      setLink(createLinkDraft());
      setLinkOptions(createLinkOptionsState());
      setError('');
      setPauseReason(FOCUS_PAUSE_REASONS[0]);
      setConnectingOneDrive(false);
      setSyncingExternal('');
      setLinkingExternalCustomer(false);
      setActiveTab('resumo');
    }
  }, [task, stageOptions, statusOptions, priorityOptions]);

  useEffect(() => {
    if (!task?._id) return undefined;
    void loadOneDriveStatus();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?._id]);

  useEffect(() => {
    if (!task?._id) return undefined;
    if (!['bling', 'auvo'].includes(link.type)) return undefined;
    if (link.lookupMode !== 'customer') return undefined;

    void loadLinkOptions(link.type);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link.type, link.lookupMode, task?._id]);

  useEffect(() => {
    const handleOneDriveMessage = (event) => {
      if (event?.data?.source !== 'task-hub-onedrive') return;

      clearPopupPoll();
      popupRef.current = null;
      setConnectingOneDrive(false);

      if (!event.data.ok) {
        setError('Não foi possível concluir a conexão com o OneDrive central.');
        return;
      }

      void loadOneDriveStatus({ surfaceError: true });
    };

    window.addEventListener('message', handleOneDriveMessage);
    return () => {
      window.removeEventListener('message', handleOneDriveMessage);
      clearPopupPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUnauthorized]);

  const whatsappAttendanceLink = useMemo(() => (
    task?.links?.find((item) => (
      item?.source === 'whatsapp'
      && item?.metadata?.type === 'whatsapp_attendance'
      && item?.id
    )) || null
  ), [task]);

  const openWhatsAppAttendanceOrigin = () => {
    if (!whatsappAttendanceLink?.id) return;
    if (onOpenWhatsAppAttendance) {
      onOpenWhatsAppAttendance(whatsappAttendanceLink.id);
      return;
    }

    const nextPath = `/whatsapp?attendanceId=${encodeURIComponent(whatsappAttendanceLink.id)}`;
    window.history.pushState({}, '', nextPath);
    window.dispatchEvent(new Event('popstate'));
  };

  if (!task) return null;

  const rawProviderState = ['bling', 'auvo'].includes(link.type)
    ? linkOptions[link.type]
    : null;
  const requestedLookupNumber = String(link.lookupNumber || '').trim();
  const activeProviderState = rawProviderState && rawProviderState.mode === link.lookupMode && rawProviderState.query === requestedLookupNumber
    ? rawProviderState
    : createProviderOptionsState({ mode: link.lookupMode, query: requestedLookupNumber });
  const selectedExternalOption = activeProviderState?.options?.find((option) => option.id === link.id) || null;
  const customerReferenceRows = buildCustomerReferenceRows(activeProviderState);
  const showCustomerReferences = ['bling', 'auvo'].includes(link.type)
    && link.lookupMode === 'customer'
    && activeProviderState?.loaded;
  const selectedExternalCustomerId = externalCustomerIdFromOption(link.type, selectedExternalOption);
  const selectedExternalCustomerLinked = selectedExternalCustomerId
    ? providerRefIdsFromState(link.type, activeProviderState).includes(selectedExternalCustomerId)
    : true;
  const canLinkSelectedExternalCustomer = Boolean(
    activeProviderState?.refs?.masterClientId &&
    selectedExternalOption &&
    selectedExternalCustomerId &&
    !selectedExternalCustomerLinked
  );
  const activeProviderHelpText = activeProviderState
    ? activeProviderState.loading
      ? `Carregando ${link.type === 'bling' ? 'propostas' : 'OS'} disponíveis...`
      : link.lookupMode === 'manual' && !requestedLookupNumber
        ? `Informe o número da ${link.type === 'bling' ? 'proposta' : 'OS'} e clique em Buscar.`
        : !activeProviderState.loaded
          ? link.lookupMode === 'manual'
            ? `Use a busca manual para localizar a ${link.type === 'bling' ? 'proposta' : 'OS'} exata mesmo quando o cliente não bater.`
            : ''
      : activeProviderState.error
        ? activeProviderState.error
        : !activeProviderState.available
          ? getProviderEmptyMessage(link.type, activeProviderState.reason, link.lookupMode)
        : activeProviderState.options.length
          ? `${activeProviderState.options.length} ${link.type === 'bling' ? 'propostas' : 'OS'} livres para vínculo. Itens já usados em qualquer tarefa ficam ocultos.`
            : `${getProviderEmptyMessage(link.type, activeProviderState.reason, link.lookupMode)}${activeProviderState.totalExcluded ? ` ${formatLinkedTaskSummary(activeProviderState.excludedOptions) || 'Os demais já estão vinculados em outras tarefas.'}` : ''}`
    : '';
  const hasExternalCustomerMismatch = selectedExternalOption?.customerName
    && task.customer?.name
    && normalizeComparableText(selectedExternalOption.customerName) !== normalizeComparableText(task.customer.name);
  const timerMatchesTask = focusTimer?.taskId === task._id;
  const timerStatusLabel = focusTimerSnapshot?.cycleCompleted
    ? 'Bloco concluído'
    : focusTimerSnapshot?.isPaused
      ? 'Pausado'
      : 'Em andamento';

  const handleRequestError = (requestError, fallbackMessage) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }

    setError(getErrorMessage(requestError, fallbackMessage));
  };

  const loadLinkOptions = async (provider, { force = false, mode = link.lookupMode, number = link.lookupNumber } = {}) => {
    if (!task?._id || !['bling', 'auvo'].includes(provider)) return;

    const currentState = linkOptions[provider];
    const safeMode = mode === 'manual' ? 'manual' : 'customer';
    const safeNumber = String(number || '').trim();
    const alreadyLoaded = currentState.loaded && currentState.mode === safeMode && currentState.query === safeNumber;
    if (!force && (currentState.loading || alreadyLoaded)) return;
    if (safeMode === 'manual' && !safeNumber) return;

    setProviderOptions(provider, {
      loading: true,
      error: '',
      mode: safeMode,
      query: safeNumber
    });

    try {
      const payload = await TasksAPI.linkOptions(task._id, provider, {
        limit: 80,
        mode: safeMode,
        number: safeMode === 'manual' ? safeNumber : undefined
      });
      const options = Array.isArray(payload?.options) ? payload.options : [];

      setProviderOptions(provider, {
        loaded: true,
        loading: false,
        available: Boolean(payload?.available),
        reason: payload?.reason || '',
        customer: payload?.customer || null,
        refs: payload?.refs || {},
        searchStrategy: payload?.searchStrategy || '',
        options,
        error: '',
        totalFetched: Number(payload?.totalFetched || options.length),
        totalAvailable: Number(payload?.totalAvailable || options.length),
        totalExcluded: Number(payload?.totalExcluded || 0),
        excludedOptions: Array.isArray(payload?.excludedOptions) ? payload.excludedOptions : [],
        mode: safeMode,
        query: safeNumber
      });

      setLink((current) => {
        if (current.type !== provider) return current;
        if (!current.id) return current;

        const stillExists = options.some((option) => option.id === current.id);
        return stillExists ? current : { ...current, id: '' };
      });
    } catch (requestError) {
      if (isUnauthorizedError(requestError)) {
        onUnauthorized?.();
        return;
      }

      setProviderOptions(provider, {
        loaded: true,
        loading: false,
        error: getErrorMessage(requestError, `Não foi possível carregar a lista de ${provider === 'bling' ? 'propostas' : 'OS'}.`),
        mode: safeMode,
        query: safeNumber,
        searchStrategy: ''
      });
    }
  };

  const submitWorkflow = async () => {
    setSaving(true);
    setError('');
    try {
      const assignee = workflow.assigneeId
        ? { id: workflow.assigneeId, name: workflow.assigneeName || workflow.assigneeId }
        : null;

      const updated = await TasksAPI.workflow(task._id, {
        stage: workflow.stage,
        status: workflow.status,
        priority: workflow.priority,
        area: workflow.areaCode ? { code: workflow.areaCode } : null,
        dueDate: workflow.dueDate || null,
        assignee,
        note: workflow.note
      });
      onTaskUpdated(updated);
    } catch (requestError) {
      handleRequestError(requestError, 'Não foi possível salvar o andamento.');
    } finally {
      setSaving(false);
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    setError('');
    try {
      const updated = await TasksAPI.comment(task._id, comment.trim());
      setComment('');
      onTaskUpdated(updated);
    } catch (requestError) {
      handleRequestError(requestError, 'Não foi possível registrar o comentário.');
    } finally {
      setSaving(false);
    }
  };

  const connectOneDrive = async () => {
    setConnectingOneDrive(true);
    setError('');

    try {
      const { url } = await IntegrationsAPI.oneDriveConnectUrl();
      const popup = window.open(url, 'task-hub-onedrive', 'width=640,height=760');

      if (!popup) {
        throw new Error('O navegador bloqueou a abertura da autenticação da Microsoft. Libere pop-ups e tente novamente.');
      }

      popupRef.current = popup;
      clearPopupPoll();
      popupPollRef.current = window.setInterval(() => {
        if (!popupRef.current?.closed) return;

        clearPopupPoll();
        popupRef.current = null;
        setConnectingOneDrive(false);
        void loadOneDriveStatus();
      }, 800);
    } catch (requestError) {
      setConnectingOneDrive(false);
      handleRequestError(requestError, 'Não foi possível iniciar a conexão com o OneDrive central.');
    }
  };

  const submitLink = async () => {
    const selectedOption = ['bling', 'auvo'].includes(link.type)
      ? activeProviderState?.options?.find((option) => option.id === link.id) || null
      : null;
    const validationError = validateLinkDraft(link, oneDriveStatus, selectedOption);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      let updated = null;

      if (link.type === 'documento' || (link.type === 'imagem' && link.mode === 'upload')) {
        const files = Array.isArray(link.files) ? link.files : [];
        for (const file of files) {
          updated = await TasksAPI.uploadOneDriveLink(task._id, {
            type: link.type,
            label: files.length === 1 ? link.label.trim() : '',
            file
          });
        }
      } else if (link.type === 'bling' || link.type === 'auvo') {
        await syncExternalProvider(link.type, {
          number: selectedOption.code || selectedOption.metadata?.numero || selectedOption.id,
          surfaceError: false
        });
        updated = await TasksAPI.addLink(task._id, buildExternalOptionPayload(link.type, selectedOption));
      } else {
        updated = await TasksAPI.addLink(task._id, buildManualLinkPayload(link));
      }

      setLink(createLinkDraft());
      onTaskUpdated(updated);
    } catch (requestError) {
      handleRequestError(requestError, 'Não foi possível vincular o item.');
    } finally {
      setSaving(false);
    }
  };

  const syncExternalProvider = async (provider, { linkId = '', number = '', surfaceError = true } = {}) => {
    if (!task?._id || !['bling', 'auvo'].includes(provider)) return null;

    const syncKey = linkId ? `${provider}:${linkId}` : provider;
    setSyncingExternal(syncKey);
    if (surfaceError) setError('');

    try {
      const payload = await TasksAPI.syncExternalLink(task._id, provider, {
        linkId,
        number
      });

      if (payload?.task) onTaskUpdated(payload.task);
      await loadLinkOptions(provider, { force: true });
      return payload;
    } catch (requestError) {
      if (surfaceError) {
        handleRequestError(requestError, `Não foi possível sincronizar ${provider === 'bling' ? 'o Bling' : 'o AUVO'}.`);
      } else if (isUnauthorizedError(requestError)) {
        onUnauthorized?.();
      } else {
        throw requestError;
      }
      return null;
    } finally {
      setSyncingExternal('');
    }
  };

  const linkSelectedExternalCustomer = async () => {
    if (!canLinkSelectedExternalCustomer || !activeProviderState?.refs?.masterClientId || !selectedExternalOption) return;

    setLinkingExternalCustomer(true);
    setError('');

    try {
      await ClientConsolidationAPI.manualLink(activeProviderState.refs.masterClientId, {
        sourceSystem: link.type,
        externalId: selectedExternalCustomerId,
        externalName: selectedExternalOption.customerName || task.customer?.name || selectedExternalOption.label,
        documentNumber: selectedExternalOption.metadata?.documentNumber || selectedExternalOption.metadata?.cnpj || '',
        rawData: {
          source: 'task_link_manual_lookup',
          provider: link.type,
          selectedOption: selectedExternalOption,
          taskId: task._id
        },
        reasons: [`Vínculo criado pela busca manual de ${link.type === 'bling' ? 'proposta Bling' : 'tarefa AUVO'} no Task Hub`],
        confidenceScore: 100
      });

      await loadLinkOptions(link.type, {
        force: true,
        mode: link.lookupMode,
        number: link.lookupNumber
      });
    } catch (requestError) {
      handleRequestError(requestError, 'Não foi possível vincular o cliente externo ao Cliente Mestre.');
    } finally {
      setLinkingExternalCustomer(false);
    }
  };

  const generateBlingReport = async (item) => {
    if (!item?.id) return;

    const reportWindow = window.open('about:blank', '_blank');
    if (!reportWindow) {
      setError('O navegador bloqueou a abertura do relatório. Libere pop-ups e tente novamente.');
      return;
    }

    reportWindow.document.write('<!doctype html><title>Carregando relatório</title><body style="font-family: Arial, sans-serif; padding: 24px;">Carregando relatório da proposta...</body>');
    reportWindow.document.close();
    setReportingLinkId(item.id);
    setError('');

    try {
      const html = await TasksAPI.getBlingReportHtml(task._id, item.id);
      const reportUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      reportWindow.location.replace(reportUrl);
    } catch (requestError) {
      reportWindow.document.open();
      reportWindow.document.write('<!doctype html><title>Erro</title><body style="font-family: Arial, sans-serif; padding: 24px;">Não foi possível abrir o relatório da proposta.</body>');
      reportWindow.document.close();
      handleRequestError(requestError, 'Não foi possível gerar o relatório da proposta.');
    } finally {
      setReportingLinkId('');
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !onDeleteTask) return;
    const confirmed = window.confirm(`Excluir a tarefa "${task.title}"? Essa ação não pode ser desfeita.`);
    if (!confirmed) return;

    setSaving(true);
    setError('');
    try {
      await onDeleteTask(task);
    } catch (requestError) {
      handleRequestError(requestError, 'Não foi possível excluir a tarefa.');
      setSaving(false);
    }
  };

  const renderOneDriveStatus = () => {
    if (oneDriveLoading) {
      return <div className="small text-muted">Consultando o OneDrive central...</div>;
    }

    if (!oneDriveStatus) {
      return <div className="small text-muted">Status do OneDrive ainda não carregado.</div>;
    }

    if (!oneDriveStatus.configured) {
      return (
        <div className="alert alert-warning py-2 mb-3">
          <div><strong>OneDrive central não configurado na API.</strong></div>
          <div className="small mt-1">
            Ajuste as variáveis: {oneDriveStatus.missing?.join(', ') || 'configuração pendente'}.
          </div>
        </div>
      );
    }

    if (oneDriveStatus.connected) {
      return (
        <div className="alert alert-success py-2 mb-3">
          <div><strong>{getOneDriveAccountLabel(oneDriveStatus)}</strong> conectado para receber arquivos.</div>
          <div className="small mt-1">
            Pasta base: {oneDriveStatus.settings?.baseFolderName || 'Task Hub'}
          </div>
        </div>
      );
    }

    return (
      <div className="alert alert-secondary py-2 mb-3">
        <div><strong>OneDrive central pronto para conexão.</strong></div>
        <div className="small mt-1">
          Conecte uma única conta para centralizar documentos e imagens desta operação.
        </div>
      </div>
    );
  };

  return (
    <div className="drawer-backdrop">
      <aside className="task-drawer shadow-lg">
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div>
            <div className="eyebrow">Detalhe da tarefa</div>
            <h2>{task.title}</h2>
            <p className="text-muted mb-0">Cliente: {task.customer?.name || 'Não informado'}</p>
          </div>
          <button className="btn btn-outline-secondary" onClick={onClose}>Fechar</button>
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

        <section className="task-focus-card mt-4">
          <div className="task-focus-card__main">
            <span className="badge text-bg-secondary">{task.status}</span>
            <h3>Próxima ação</h3>
            <p>{task.lastActivity?.summary || task.description || 'Definir o próximo passo desta tarefa.'}</p>
          </div>
          <div className="task-focus-card__facts">
            <div><span>Prazo</span><strong>{formatDate(task.dueDate)}</strong></div>
            <div><span>Responsável</span><strong>{task.assignee?.name || 'Não definido'}</strong></div>
            <div><span>Etapa</span><strong>{formatStage(task.stage)}</strong></div>
          </div>
        </section>

        {whatsappAttendanceLink && (
          <section className="panel-section mt-3">
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <div>
                <div className="eyebrow">Origem</div>
                <strong>Atendimento WhatsApp</strong>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={openWhatsAppAttendanceOrigin}
              >
                Abrir atendimento
              </button>
            </div>
          </section>
        )}

        <div className="task-tabs mt-3" role="tablist" aria-label="Detalhes da tarefa">
          {[
            ['resumo', 'Resumo'],
            ['acao', 'Ação'],
            ['vinculos', `Vínculos ${task.links?.length || 0}`],
            ['historico', `Histórico ${task.events?.length || 0}`],
            ['foco', 'Foco'],
            ['mais', 'Mais']
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`task-tab ${activeTab === value ? 'task-tab--active' : ''}`}
              onClick={() => setActiveTab(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'mais' && (
        <section className="panel-section mt-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <h3>Mais dados da tarefa</h3>
            <strong className="small text-muted">{task.priority} • {task.area?.name || 'Sem área'}</strong>
          </div>
          <div className="info-list mt-3">
            <div><span>Status</span><strong>{task.status}</strong></div>
            <div><span>Etapa</span><strong>{formatStage(task.stage)}</strong></div>
            <div><span>Prioridade</span><strong>{task.priority}</strong></div>
            <div><span>Área</span><strong>{task.area?.name || 'Não definida'}</strong></div>
            <div><span>Última movimentação</span><strong>{formatDateTime(task.lastMovementAt)}</strong></div>
          </div>
          <div className="mt-3">
            <div className="small text-muted mb-1">Relato inicial</div>
            <p className="mb-0">{task.description || 'Sem relato inicial.'}</p>
          </div>
        </section>
        )}

        {activeTab === 'foco' && (
        <section className="panel-section mt-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <h3>Foco nesta tarefa</h3>
            <strong className="small text-muted">{timerMatchesTask ? timerStatusLabel : 'Timer'}</strong>
          </div>
          <div className="focus-timer-panel mt-3">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <div className="eyebrow">Sessão de foco</div>
                  <strong>{timerMatchesTask ? timerStatusLabel : 'Sem timer ativo nesta tarefa'}</strong>
                  <div className="small text-muted mt-1">
                    {timerMatchesTask
                      ? focusTimer?.durationMinutes > 0
                        ? focusTimerSnapshot?.cycleCompleted
                          ? `Tempo total: ${formatTimerDuration(focusTimerSnapshot?.elapsedMs || 0)}`
                          : focusTimerSnapshot?.isPaused
                            ? `Pausado em ${formatTimerDuration(focusTimerSnapshot?.elapsedMs || 0)}`
                            : `Restante: ${formatTimerDuration(focusTimerSnapshot?.remainingMs || 0)}`
                        : `Tempo em foco: ${formatTimerDuration(focusTimerSnapshot?.elapsedMs || 0)}`
                      : focusTimer?.taskId
                        ? `Existe outra sessão ativa em "${focusTimer.taskTitle}".`
                        : 'Use um bloco curto para segurar a atenção nesta demanda.'}
                  </div>
                </div>
                {timerMatchesTask && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onFinishFocusTimer?.({ cancel: false })}>
                    Encerrar e registrar
                  </button>
                )}
              </div>

              {!focusTimer?.taskId && (
                <div className="d-flex gap-2 flex-wrap mt-3">
                  {FOCUS_TIMER_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => onStartFocusTimer?.(task, preset.value)}
                    >
                      {preset.value > 0 ? `Iniciar ${preset.label}` : 'Iniciar livre'}
                    </button>
                  ))}
                </div>
              )}

              {focusTimer?.taskId && !timerMatchesTask && (
                <div className="d-flex gap-2 flex-wrap mt-3">
                  {FOCUS_TIMER_PRESETS.map((preset) => (
                    <button
                      key={`switch-${preset.label}`}
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => onStartFocusTimer?.(task, preset.value)}
                    >
                      {preset.value > 0 ? `Trocar para ${preset.label}` : 'Trocar para livre'}
                    </button>
                  ))}
                </div>
              )}

              {timerMatchesTask && focusTimerSnapshot?.isRunning && (
                <div className="mt-3">
                  <label className="form-label">Motivo da pausa</label>
                  <div className="d-flex gap-2 flex-wrap">
                    <select className="form-select" value={pauseReason} onChange={(event) => setPauseReason(event.target.value)}>
                      {FOCUS_PAUSE_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                    </select>
                    <button type="button" className="btn btn-outline-warning" onClick={() => onPauseFocusTimer?.(pauseReason)}>
                      Pausar
                    </button>
                  </div>
                </div>
              )}

              {timerMatchesTask && focusTimerSnapshot?.isPaused && (
                <div className="d-flex gap-2 flex-wrap mt-3">
                  <button type="button" className="btn btn-outline-success" onClick={() => onResumeFocusTimer?.()}>
                    Retomar
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => onFinishFocusTimer?.({ cancel: false })}>
                    Fechar ciclo
                  </button>
                </div>
              )}
            </div>
        </section>
        )}

        {activeTab === 'acao' && (
        <section className="panel-section mt-3">
          <div className="mt-3">
            <h3>Passar para o próximo passo</h3>
            <div className="d-grid gap-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Etapa</label>
                  <select className="form-select" value={workflow.stage} onChange={(event) => setWorkflow((current) => ({ ...current, stage: event.target.value }))}>
                    {stageOptions.map((stage) => <option key={stage} value={stage}>{formatStage(stage)}</option>)}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={workflow.status} onChange={(event) => setWorkflow((current) => ({ ...current, status: event.target.value }))}>
                    {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Prioridade</label>
                  <select className="form-select" value={workflow.priority} onChange={(event) => setWorkflow((current) => ({ ...current, priority: event.target.value }))}>
                    {priorityOptions.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Área</label>
                  <select className="form-select" value={workflow.areaCode} onChange={(event) => setWorkflow((current) => ({ ...current, areaCode: event.target.value }))}>
                    <option value="">Sem área</option>
                    {areaOptions.map((area) => <option key={area.code} value={area.code}>{area.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Próximo responsável</label>
                  <select
                    className="form-select"
                    value={workflow.assigneeId}
                    onChange={(event) => {
                      const next = userOptions.find((user) => user.id === event.target.value);
                      setWorkflow((current) => ({
                        ...current,
                        assigneeId: event.target.value,
                        assigneeName: next?.name || current.assigneeName
                      }));
                    }}
                  >
                    <option value="">Sem responsável</option>
                    {userOptions.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Prazo do próximo passo</label>
                  <input
                    className="form-control"
                    type="date"
                    value={workflow.dueDate}
                    onChange={(event) => setWorkflow((current) => ({ ...current, dueDate: event.target.value }))}
                  />
                </div>
              </div>

              <div>
                <div className="small text-muted">
                  Sempre que a tarefa avançar, ajuste aqui o prazo real do próximo responsável.
                </div>
                {workflow.assigneeId && (
                  <input
                    className="form-control mt-2"
                    placeholder="Nome do responsável"
                    value={workflow.assigneeName}
                    onChange={(event) => setWorkflow((current) => ({ ...current, assigneeName: event.target.value }))}
                  />
                )}
              </div>

              <div>
                <label className="form-label">Relato do andamento ou do repasse</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={workflow.note}
                  onChange={(event) => setWorkflow((current) => ({ ...current, note: event.target.value }))}
                  placeholder="Descreva o que foi feito e o que o próximo responsável precisa seguir."
                />
              </div>

              <button className="btn btn-primary" onClick={submitWorkflow} disabled={saving}>Salvar andamento</button>
            </div>
          </div>
        </section>
        )}

        {activeTab === 'historico' && (
        <section className="panel-section mt-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h3>Timeline operacional</h3>
              <p className="text-muted small mb-0">Histórico completo da tarefa.</p>
            </div>
            <div className="small text-muted">{task.events?.length || 0} eventos</div>
          </div>
          <div className="timeline mt-3">
            {task.events?.slice().reverse().map((event, index) => (
              <div className="timeline-item" key={`${event.type}-${event.at}-${index}`}>
                <div className="d-flex justify-content-between gap-3 flex-wrap">
                  <strong>{getEventLabel(event)}</strong>
                  <span className="text-muted small">{formatDateTime(event.at)}</span>
                </div>
                <div className="small text-muted">Por {event.by?.name || 'Sistema'}</div>
                {renderTimelineDetails(event)}
              </div>
            ))}
            {!task.events?.length && <div className="small text-muted">Nenhuma movimentação registrada ainda.</div>}
          </div>
        </section>
        )}

        {activeTab === 'acao' && (
        <div className="drawer-grid mt-3">
          <section className="panel-section">
            <div className="mt-3">
            <h3>Log da tarefa</h3>
            <textarea
              className="form-control"
              rows={3}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Registre aqui uma atualização rápida do trabalho."
            />
            <button className="btn btn-outline-primary mt-3" onClick={submitComment} disabled={saving}>Adicionar no log</button>
            </div>
          </section>
        </div>
        )}

        {activeTab === 'vinculos' && (
        <section className="panel-section mt-3">
            <div className="mt-3">
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <h3>Documentos e vínculos</h3>
                <p className="text-muted small mb-0">
                  Escolha o tipo do vínculo e o formulário abaixo ajusta automaticamente o que precisa ser informado.
                </p>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => loadOneDriveStatus({ surfaceError: true })} disabled={oneDriveLoading || saving}>
                  Atualizar status
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={connectOneDrive}
                  disabled={connectingOneDrive || saving || !oneDriveStatus?.configured}
                >
                  {oneDriveStatus?.connected ? 'Reconectar OneDrive' : 'Conectar OneDrive'}
                </button>
              </div>
            </div>

            <div className="mt-3">
              {renderOneDriveStatus()}
              {oneDriveStatus?.lastError && !oneDriveStatus?.connected && (
                <div className="small text-danger mb-3">{oneDriveStatus.lastError}</div>
              )}
            </div>

            <div className="d-grid gap-2">
              <div>
                <label className="form-label">Tipo de vínculo</label>
                <select
                  className="form-select"
                  value={link.type}
                  onChange={(event) => setLink(createLinkDraft(event.target.value))}
                >
                  {LINK_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>

              {link.type === 'imagem' && (
                <div>
                  <label className="form-label">Origem da imagem</label>
                  <select
                    className="form-select"
                    value={link.mode}
                    onChange={(event) => setLink((current) => ({
                      ...current,
                      mode: event.target.value,
                      files: [],
                      url: '',
                      resetToken: `${current.type}-${Date.now()}-${Math.random()}`
                    }))}
                  >
                    {IMAGE_MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
              )}

              {!['bling', 'auvo'].includes(link.type) && (
                <div>
                  <label className="form-label">Nome do documento ou vínculo</label>
                  <input
                    className="form-control"
                    value={link.label}
                    onChange={(event) => setLink((current) => ({ ...current, label: event.target.value }))}
                    placeholder={link.type === 'imagem' ? 'Ex.: Foto do local' : 'Ex.: Contrato assinado'}
                  />
                </div>
              )}

              {(link.type === 'documento' || (link.type === 'imagem' && link.mode === 'upload')) && (
                <div>
                  <label className="form-label">
                    {link.type === 'imagem' ? 'Imagem do computador' : 'Arquivo do computador'}
                  </label>
                  <input
                    key={link.resetToken}
                    className="form-control"
                    type="file"
                    multiple
                    accept={link.type === 'imagem' ? 'image/*' : undefined}
                    onChange={(event) => setLink((current) => ({ ...current, files: Array.from(event.target.files || []) }))}
                  />
                  <div className="small text-muted mt-1">
                    {link.files?.length
                      ? `${link.files.length} arquivo${link.files.length > 1 ? 's' : ''}: ${link.files.map((file) => `${file.name} • ${formatFileSize(file.size)}`).join('; ')}`
                      : 'Nenhum arquivo selecionado.'}
                  </div>
                </div>
              )}

              {link.type === 'imagem' && link.mode === 'url' && (
                <div>
                  <label className="form-label">URL da imagem</label>
                  <input
                    className="form-control"
                    value={link.url}
                    onChange={(event) => setLink((current) => ({ ...current, url: event.target.value }))}
                    placeholder="https://..."
                  />
                  <div className="small text-muted mt-1">
                    Nesse modo a tarefa salva o vínculo externo sem copiar o arquivo para o OneDrive.
                  </div>
                </div>
              )}

              {(link.type === 'bling' || link.type === 'auvo') && (
                <div className="border rounded-3 p-3">
                  <div>
                    <label className="form-label">Modo de busca</label>
                    <select
                      className="form-select"
                      value={link.lookupMode}
                      onChange={(event) => setLink((current) => ({
                        ...current,
                        lookupMode: event.target.value,
                        lookupNumber: '',
                        id: ''
                      }))}
                    >
                      {PROVIDER_LOOKUP_MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </div>

                  {link.lookupMode === 'manual' && (
                    <div className="mt-3">
                      <label className="form-label">
                        {link.type === 'bling' ? 'Número da proposta' : 'Número da OS'}
                      </label>
                      <div className="d-flex gap-2">
                        <input
                          className="form-control"
                          value={link.lookupNumber}
                          onChange={(event) => setLink((current) => ({ ...current, lookupNumber: event.target.value, id: '' }))}
                          placeholder={link.type === 'bling' ? 'Ex.: 12345' : 'Ex.: 98765'}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => loadLinkOptions(link.type, { force: true, mode: 'manual', number: link.lookupNumber })}
                          disabled={activeProviderState?.loading || saving || !String(link.lookupNumber || '').trim()}
                        >
                          Buscar
                        </button>
                      </div>
                      <div className="small text-muted mt-1">
                        Use este modo quando a proposta ou a OS estiver cadastrada com outro cliente no sistema externo.
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <div>
                      <label className="form-label mb-1">
                        {link.lookupMode === 'manual'
                          ? link.type === 'bling' ? 'Resultados por número informado' : 'Resultados por número informado'
                          : link.type === 'bling' ? 'Propostas disponíveis do cliente' : 'OS disponíveis do cliente'}
                      </label>
                      <div className="small text-muted">
                        {activeProviderHelpText}
                      </div>
                      {showCustomerReferences && (
                        <div className="external-ref-grid mt-2">
                          {customerReferenceRows.map((row) => (
                            <div key={row.label}>
                              <span>{row.label}</span>
                              <strong className={row.value === 'Nao informado' ? 'text-muted' : ''}>{row.value}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                      {activeProviderState?.searchStrategy === 'customer_name_fallback' && (
                        <div className="small text-warning mt-1">
                          Nenhuma OS foi localizada pelo código integrado. A lista abaixo veio por aproximação de nome do cliente.
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        type="button"
                        onClick={() => loadLinkOptions(link.type, { force: true })}
                        disabled={activeProviderState?.loading || saving || Boolean(syncingExternal) || (link.lookupMode === 'manual' && !requestedLookupNumber)}
                      >
                        {link.lookupMode === 'manual' ? 'Buscar novamente' : 'Atualizar lista'}
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        type="button"
                        onClick={() => syncExternalProvider(link.type, { number: requestedLookupNumber || selectedExternalOption?.code || selectedExternalOption?.id })}
                        disabled={
                          activeProviderState?.loading ||
                          saving ||
                          Boolean(syncingExternal) ||
                          (link.type === 'bling' && !requestedLookupNumber && !selectedExternalOption?.code && !selectedExternalOption?.id)
                        }
                      >
                        {syncingExternal === link.type ? 'Sincronizando...' : `Sincronizar ${link.type === 'bling' ? 'Bling' : 'AUVO'}`}
                      </button>
                    </div>
                  </div>

                  <OperationStatus
                    active={Boolean(activeProviderState?.loading || syncingExternal)}
                    title={syncingExternal ? 'Sincronizando dados externos' : 'Buscando opções externas'}
                    steps={[
                      activeProviderState?.loading && `Consultando ${link.type === 'bling' ? 'propostas do Bling' : 'OS do AUVO'} pelo cec_api_integra.`,
                      syncingExternal && `Atualizando dados do ${link.type === 'bling' ? 'Bling' : 'AUVO'}.`,
                      'Aguardando resposta da integração.',
                    ]}
                  />

                  <div>
                    <label className="form-label mt-3">
                      {link.type === 'bling' ? 'Selecione a proposta' : 'Selecione a OS'}
                    </label>
                    <select
                      className="form-control"
                      value={link.id}
                      onChange={(event) => setLink((current) => ({ ...current, id: event.target.value }))}
                      disabled={
                        activeProviderState?.loading ||
                        !activeProviderState?.available ||
                        !activeProviderState?.options?.length
                      }
                    >
                      <option value="">
                        {activeProviderState?.loading
                          ? 'Carregando opções...'
                          : link.lookupMode === 'manual'
                            ? link.type === 'bling'
                              ? 'Selecione a proposta encontrada'
                              : 'Selecione a OS encontrada'
                            : link.type === 'bling'
                              ? 'Selecione uma proposta disponível'
                              : 'Selecione uma OS disponível'}
                      </option>
                      {activeProviderState?.options?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                          {option.code ? ` • ${option.code}` : ''}
                          {option.status ? ` • ${option.status}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedExternalOption && (
                    <div className="mt-3 small">
                      <div><strong>{selectedExternalOption.label}</strong></div>
                      {selectedExternalOption.code && <div className="text-muted">Código: {selectedExternalOption.code}</div>}
                      {selectedExternalOption.customerName && <div className="text-muted">Cliente: {selectedExternalOption.customerName}</div>}
                      {selectedExternalCustomerId && (
                        <div className="text-muted">
                          ID cliente {link.type === 'bling' ? 'Bling' : 'AUVO'}: {selectedExternalCustomerId}
                        </div>
                      )}
                      {(selectedExternalOption.status || selectedExternalOption.date || selectedExternalOption.amount !== null) && (
                        <div className="text-muted">
                          {[selectedExternalOption.status, formatOptionDate(selectedExternalOption.date), formatCurrency(selectedExternalOption.amount)]
                            .filter(Boolean)
                            .join(' • ')}
                        </div>
                      )}
                      {selectedExternalOption.url && (
                        <a className="d-inline-block mt-2" href={selectedExternalOption.url} target="_blank" rel="noreferrer">
                          Abrir no {link.type === 'bling' ? 'Bling' : 'AUVO'}
                        </a>
                      )}
                      {hasExternalCustomerMismatch && (
                        <div className="alert alert-warning py-2 mt-3 mb-0">
                          O cliente do vínculo externo é <strong>{selectedExternalOption.customerName}</strong>, mas a tarefa está com <strong>{task.customer?.name}</strong>.
                        </div>
                      )}
                      {canLinkSelectedExternalCustomer && (
                        <div className="alert alert-info py-2 mt-3 mb-0">
                          <div>
                            Este cliente {link.type === 'bling' ? 'Bling' : 'AUVO'} ainda não está vinculado ao Cliente Mestre desta tarefa.
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-2"
                            onClick={linkSelectedExternalCustomer}
                            disabled={linkingExternalCustomer || saving || activeProviderState?.loading}
                          >
                            {linkingExternalCustomer ? 'Vinculando cliente...' : 'Vincular cliente ao Cliente Mestre'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              className="btn btn-outline-primary mt-3"
              onClick={submitLink}
              disabled={saving || connectingOneDrive || activeProviderState?.loading}
            >
              {link.type === 'documento' || (link.type === 'imagem' && link.mode === 'upload')
                ? link.files?.length > 1
                  ? `Enviar ${link.files.length} arquivos e vincular`
                  : 'Enviar para o OneDrive e vincular'
                : 'Vincular item'}
            </button>

            <div className="timeline mt-3">
              {task.links?.length ? task.links.map((item, index) => (
                <div className="timeline-item" key={`${item.id || item.url || item.source}-${index}`}>
                  <strong>{item.label || item.fileName || getLinkTypeLabel(item.type)}</strong>
                  <div className="text-muted small">{getLinkDescription(item)}</div>
                  {item.fileName && item.fileName !== item.label && <div className="small mt-1">Arquivo: {item.fileName}</div>}
                  {item.folderPath && <div className="small text-muted mt-1">Pasta: {item.folderPath}</div>}
                  {item.id && <div className="small text-muted mt-1">ID: {item.id}</div>}
                  <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                    {item.url && <a className="d-inline-block" href={item.url} target="_blank" rel="noreferrer">Abrir vínculo</a>}
                    {item.type === 'bling' && (
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        type="button"
                        onClick={() => generateBlingReport(item)}
                        disabled={saving || connectingOneDrive || reportingLinkId === item.id}
                      >
                        {reportingLinkId === item.id ? 'Abrindo relatório...' : 'Abrir relatório'}
                      </button>
                    )}
                    {(item.type === 'bling' || item.type === 'auvo') && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        type="button"
                        onClick={() => syncExternalProvider(item.type, {
                          linkId: item.id,
                          number: item.code || item.metadata?.numero || item.id
                        })}
                        disabled={saving || connectingOneDrive || Boolean(syncingExternal)}
                      >
                        {syncingExternal === `${item.type}:${item.id}` ? 'Sincronizando...' : 'Atualizar dados'}
                      </button>
                    )}
                  </div>
                </div>
              )) : <div className="text-muted small">Nenhum vínculo ainda.</div>}
            </div>
            </div>
        </section>
        )}

        {canDelete && (
          <section className="panel-section danger-zone mt-4">
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <div>
                <h3>Exclusão da tarefa</h3>
                <p className="text-muted mb-0">Somente administradores podem remover tarefas do sistema.</p>
              </div>
              <button className="btn btn-outline-danger" onClick={handleDelete} disabled={saving}>
                Excluir tarefa
              </button>
            </div>
          </section>
        )}
      </aside>
    </div>
  );
}
